// ABOUTME: Settings view on the warehouse Email settings page.
// ABOUTME: Shows the mailbox shipper claims are sent through and the mail button color settings.

import { Box, useTheme } from "@mui/material";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../theme";
import Subheading from "../../../../../components/theme/text/Subheading";
import Text from "../../../../../components/theme/text/Text";
import EmailButtonColorSettings, {
    DEFAULT_EMAIL_BUTTON_COLOR,
    DEFAULT_EMAIL_BUTTON_TEXT_COLOR,
} from "../../../../../components/global/EmailTemplates/EmailButtonColorSettings";
import {
    useGetSettingsQuery,
    useUpdateSettingMutation,
} from "../../../../../newapi/global/generalApiSlice";
import { useGetExternalChannelsQuery } from "../../../../../newapi/externalchannel/externalChannelSlice";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../utils/permissions";
import { getExternalChannelLogo } from "../../../../../utils/logomapper";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

const BUTTON_COLOR_KEY = "email_button_color";
const BUTTON_TEXT_COLOR_KEY = "email_button_text_color";

const EmailSettings = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const hasManagePermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.SETTINGS_GENERAL, UserPrivileges.SETTINGS_ALL])
    );

    const { data: settingsData } = useGetSettingsQuery("email");
    const [updateSetting] = useUpdateSettingMutation();
    // Same lookup as the claims section on the shipper: the fulfilment's own mailbox channels.
    const { data: channelData } = useGetExternalChannelsQuery({
        args: { page: 1, size: 1, body: { all_credentials: true, channel_type: ["outlook", "smtpimap"], without_relation: true } }
    });

    useEffect(() => {
        props.replaceView({
            ...props.config,
        });
    }, []);

    const settings = settingsData?.data ?? settingsData ?? [];
    const channels = channelData?.data ?? channelData ?? [];
    // Only shipper claims leave through a connected mailbox (Outlook or SMTP/IMAP); every other
    // mail is sent by StockHub.
    const mailboxChannel = channels.find(
        (channel) => (channel.channel_type === "outlook" && channel.credentials?.connected)
            || (channel.channel_type === "smtpimap" && channel.credentials?.hasPassword)
    );

    const getSetting = (key) => settings.find((setting) => setting.key === key);

    const saveColor = (key) => (value) => {
        updateSetting({
            key,
            value,
            warehouseId: getSetting(key)?.warehouseId ?? -1,
        });
    };

    return (
        <Box
            sx={{
                height: 1,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    width: 1,
                    overflow: "auto",
                    background:
                        theme.palette.mode === "dark"
                            ? colors.grey[300]
                            : colors.bg["tertiary"],
                    paddingX: 3,
                    paddingTop: 4,
                    paddingBottom: 4,
                    height: 1,
                    position: "relative",
                    borderTopLeftRadius: 24,
                    borderTopRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": {
                        paddingTop: 2,
                        paddingX: 2,
                        background: colors.bg["tertiary"],
                    },
                }}
            >
                <Box
                    sx={{
                        background:
                            theme.palette.mode === "dark"
                                ? colors.grey[400]
                                : colors.grey[0],
                        border:
                            theme.palette.mode === "dark"
                                ? null
                                : `1px solid ${colors.grey[100]}`,
                        boxShadow:
                            theme.palette.mode === "dark"
                                ? null
                                : `0px 2px 4px ${colors.grey[200]}`,
                        borderRadius: 3,
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                    }}
                >
                    <Subheading>{t("email_sending_channel")}</Subheading>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                        }}
                    >
                        {mailboxChannel ? (
                            <>
                                {getExternalChannelLogo(mailboxChannel.channel_type)}
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Text semibold>{t("email_sending_channel_mailbox")}</Text>
                                    <Text light>{mailboxChannel.credentials.account}</Text>
                                </Box>
                            </>
                        ) : (
                            <>
                                <EmailOutlinedIcon sx={{ color: colors.txt["secondary"] }} />
                                <Text semibold>{t("email_sending_channel_default")}</Text>
                            </>
                        )}
                    </Box>
                </Box>

                <EmailButtonColorSettings
                    buttonColor={getSetting(BUTTON_COLOR_KEY)?.value ?? DEFAULT_EMAIL_BUTTON_COLOR}
                    buttonTextColor={getSetting(BUTTON_TEXT_COLOR_KEY)?.value ?? DEFAULT_EMAIL_BUTTON_TEXT_COLOR}
                    onButtonColorChange={saveColor(BUTTON_COLOR_KEY)}
                    onButtonTextColorChange={saveColor(BUTTON_TEXT_COLOR_KEY)}
                    disabled={!hasManagePermission}
                />
            </Box>
        </Box>
    );
};

export default EmailSettings;
